/**
 * Unified workbench realtime: legacy API Gateway WebSocket and AppSync Events share one
 * consumer contract (`websocket-message`). Dedupes duplicate deliveries when both transports
 * carry the same logical update.
 */

export type WorkbenchTransport = 'legacy' | 'appsync';

export type WorkbenchRealtimeDetail = Record<string, unknown> & {
  __transport?: WorkbenchTransport;
};

const DEDUPE_WINDOW_MS = 2_500;
const PRUNE_AGE_MS = 120_000;
const MAX_KEYS = 800;

const recent = new Map<string, number>();

function prune(now: number): void {
  for (const [k, t] of recent) {
    if (now - t > PRUNE_AGE_MS) recent.delete(k);
  }
  if (recent.size <= MAX_KEYS) return;
  const entries = [...recent.entries()].sort((a, b) => a[1] - b[1]);
  const drop = Math.ceil(entries.length / 2);
  for (let i = 0; i < drop; i += 1) recent.delete(entries[i][0]);
}

/**
 * Stable per-event dedupe key.
 *
 * Preference order:
 *   1. ``eventId`` — set by ``stepflow_core.events`` on every canonical envelope
 *      (UUID minted server-side, identical across transports and replays). The
 *      reliable path for modern events.
 *   2. ``_appsyncEventId`` — set by the normalizer from the AppSync ``data.event[]``
 *      wrapper's ``id``. Deterministic per AppSync delivery; covers legacy
 *      ``status_update`` envelopes that predate the ``eventId`` rollout.
 *   3. Composite (type + task + status + timestamp) — keeps ``timestamp`` in
 *      the key *on purpose*: when a stream ships multiple real progress pings
 *      with the same ``status`` (e.g. ``ARCHITECTING`` every few seconds)
 *      these need to reach the UI as distinct events. Two transports both
 *      delivering the *same* ping will carry the same server-stamped
 *      timestamp, so they still collapse. This path is only hit for events
 *      that somehow lost both IDs — rare in practice.
 *   4. Whole-object ``JSON.stringify`` as last resort. Internal markers
 *      (``__transport``, ``_appsyncEventId``) are stripped so the same logical
 *      event doesn't hash differently across transports.
 */
function dedupeKey(detail: Record<string, unknown>): string {
  if (typeof detail.eventId === 'string' && detail.eventId.length > 0) {
    return `ev:${detail.eventId}`;
  }
  if (typeof detail._appsyncEventId === 'string' && detail._appsyncEventId.length > 0) {
    return `as:${detail._appsyncEventId}`;
  }
  const t = detail.type ?? detail.eventType;
  if (t === 'status_update') {
    const taskId = String(detail.taskId ?? '');
    const status = String(detail.status ?? '');
    const ts = String(detail.timestamp ?? '');
    return `su:${taskId}:${status}:${ts}`;
  }
  if (t === 'log') {
    return `log:${detail.taskId ?? ''}:${detail.timestamp ?? ''}:${String(detail.message ?? '').slice(0, 200)}`;
  }
  if (t === 'connection_established' || detail.eventType === 'connection_established') {
    return `conn:${detail.connectionId ?? ''}`;
  }
  try {
    const {
      _appsyncEventId: _a,
      __transport: _tr,
      ...stable
    } = detail;
    void _a; void _tr; // mark stripped keys as intentionally unused
    return `raw:${JSON.stringify(stable)}`;
  } catch {
    return `raw:${String(t)}`;
  }
}

/**
 * Dispatches a normalized workbench message. Returns false if skipped as a duplicate.
 */
export function dispatchWorkbenchRealtime(
  detail: Record<string, unknown>,
  source: WorkbenchTransport,
): boolean {
  const key = dedupeKey(detail);
  const now = Date.now();
  prune(now);
  const prev = recent.get(key);
  if (prev !== undefined && now - prev < DEDUPE_WINDOW_MS) {
    return false;
  }
  recent.set(key, now);
  const out: WorkbenchRealtimeDetail = {
    ...detail,
    __transport: source,
  };
  window.dispatchEvent(new CustomEvent('websocket-message', { detail: out }));
  return true;
}

/** AppSync subscriber delivers JSON objects; ensure same shape as legacy WS `detail`. */
export function normalizeAppSyncWorkbenchPayload(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;

  // Realtime ``data.event[]`` entries are JSON strings of ``onPublish`` output:
  //   { id, payload: { ...envelope, server_timestamp } }
  // Legacy WS delivered the envelope at the top level. Unwrap so BuildTab's
  // ``status_update`` / ``STATUS_UPDATE`` handlers see ``type``, ``status``,
  // ``taskId``, and ``payload`` where they expect them.
  let pl: unknown = o.payload;
  if (typeof pl === 'string') {
    try {
      pl = JSON.parse(pl) as Record<string, unknown>;
    } catch {
      pl = null;
    }
  }
  if (
    pl &&
    typeof pl === 'object' &&
    !Array.isArray(pl) &&
    (typeof (pl as Record<string, unknown>).eventType === 'string' ||
      typeof (pl as Record<string, unknown>).type === 'string')
  ) {
    const inner = { ...(pl as Record<string, unknown>) };
    if (o.id !== undefined) {
      inner._appsyncEventId = o.id;
    }
    const et = String(inner.eventType || '').toUpperCase();
    if (et === 'STATUS_UPDATE' && typeof inner.type !== 'string') {
      inner.type = 'status_update';
    }
    return inner;
  }

  if (typeof o.type === 'string' || typeof o.eventType === 'string') {
    const out = { ...o };
    const et = String(out.eventType || '').toUpperCase();
    if (et === 'STATUS_UPDATE' && typeof out.type !== 'string') {
      (out as Record<string, unknown>).type = 'status_update';
    }
    return out;
  }
  return o;
}
